import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ReportModuleComponent } from 'src/app/report-container/report.component';
import { ModuleConfiguration } from 'src/app/app.module';
import { SENSOR_LOCALIZATION_MODULE_DETAILS } from './sensor-localization.moduleDetails';
import {
  SensorLocalizationConfig,
  SensorLocalizationSensor,
} from '../sensor-localization.types';
import {
  AlarmsService,
  SensorStatus,
  SensorsService,
} from 'src/app/api/generated';
import { ReportModulesService } from 'src/app/services/report-modules/report-modules.service';
import { UserService } from 'src/app/user.service';
import proj4 from 'proj4';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { SensorDetailsDialogComponent } from '../details-dialog/sensor-details-dialog.component';

import WMTS from 'ol/source/WMTS.js';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import TileLayer from 'ol/layer/Tile';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import View from 'ol/View';
import { Map as olMap, Feature, Overlay } from 'ol';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as SourceVector } from 'ol/source';
import { Point } from 'ol/geom';
import {
  Fill,
  Style,
  Text,
  Circle as CircleStyle,
  Stroke,
  RegularShape,
} from 'ol/style';
import { defaults, DragPan, MouseWheelZoom } from 'ol/interaction';
import { platformModifierKeyOnly } from 'ol/events/condition';
import { EMPTY, firstValueFrom, Subject, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import {
  formatAlarmComparison,
  formatAlarmDescriptor,
  formatMaximumSeverity,
  formatSensorMapLabel,
  getAlarmLevelStatusLabel,
} from '../alarm-display';
import {
  buildSensorLegend,
  getSensorStatusColor,
  SensorLegend,
} from '../sensor-legend';

const SENSOR_STATUS_POLL_INTERVAL_MS = 60_000;

@Component({
    selector: 'sensor-localization-module',
    templateUrl: './sensor-localization.component.html',
    styleUrls: ['./sensor-localization.component.scss'],
    standalone: false
})
export class SensorLocalizationComponent
  implements OnInit, AfterViewInit, OnDestroy, ReportModuleComponent
{
  @Input() config: SensorLocalizationConfig;
  @ViewChild('mapElement') mapElement: ElementRef;
  @ViewChild('popUpContent') popUpContent: ElementRef;
  @ViewChild('popUpContainer') popUpContainer: ElementRef;

  public static moduleDetails: ModuleConfiguration =
    SENSOR_LOCALIZATION_MODULE_DETAILS;
  public legend: SensorLegend = { shapeEntries: [], statusEntries: [] };

  private map: olMap;
  private vectorSource: SourceVector = new SourceVector({ features: [] });
  private vectorLayer: VectorLayer = new VectorLayer({
    source: this.vectorSource,
    zIndex: 99,
  });
  private readonly destroy$ = new Subject<void>();
  private readonly sensorStatuses = new Map<string, SensorStatus>();
  private renderedSensors: SensorLocalizationSensor[] = [];
  private statusPollingStarted = false;
  private destroyed = false;

  constructor(
    private ngZone: NgZone,
    private sensorsService: SensorsService,
    public readonly reportModuleService: ReportModulesService,
    public userService: UserService,
    private dialog: MatDialog,
    private alarmsService: AlarmsService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (!this.map) {
      this.ngZone.runOutsideAngular(() => this.initMap());
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.map?.setTarget(undefined);
  }

  private async initMap() {
    const parser = new WMTSCapabilities();

    const trigonosCapabilities = await fetch(
      environment.trigonosGeoServerURL,
      {}
    );
    const responseText = await trigonosCapabilities.text();
    const trigonosCapabilitiesParsed = parser.read(responseText);
    const trigonosLayers = trigonosCapabilitiesParsed.Contents.Layer;

    if (this.destroyed) {
      return;
    }

    const trigonosTileLayers = trigonosLayers.map(
      (layer) =>
        new TileLayer({
          name: layer.Identifier,
          title: layer.Title,
          opacity: 1,
          source: new WMTS(
            optionsFromCapabilities(trigonosCapabilitiesParsed, {
              layer: layer.Identifier,
            })
          ),
          type: 'base',
          visible: true,
        })
    );

    const basemapText = await (await fetch(environment.basemapURL)).text();
    const baseParser = new WMTSCapabilities();
    const baseCaps = baseParser.read(basemapText);
    const optionsImageLayer = optionsFromCapabilities(baseCaps, {
      layer: 'bmaporthofoto30cm',
      matrixSet: 'EPSG:3857',
      style: 'raster',
    });
    const optionsGelaendeLayer = optionsFromCapabilities(baseCaps, {
      layer: 'bmapgelaende',
      matrixSet: 'EPSG:3857',
    });

    if (this.destroyed) {
      return;
    }

    this.map = new olMap({
      layers: [
        new TileLayer({
          name: 'shading',
          title: 'Gelände',
          opacity: 1,
          source: new WMTS(optionsGelaendeLayer),
          type: 'base',
        }),
        new TileLayer({
          name: 'image',
          title: 'Orthofoto',
          opacity: 1,
          source: new WMTS(optionsImageLayer),
          type: 'base',
        }),
        ...trigonosTileLayers,
        this.vectorLayer,
      ],
      target: this.mapElement.nativeElement,
      view: new View({
        // center: [47.265773684377756, 11.447103141861486],
        center: [1577650, 6038030],
        // center: [0, 0],
        zoom: 6,
      }),
      controls: [],
      interactions: defaults({ dragPan: false, mouseWheelZoom: false }).extend([
        new DragPan({
          condition: function (event) {
            return (
              this.getPointerCount() === 2 || 'ontouchstart' in window === false
            );
          },
        }),
        new MouseWheelZoom({
          condition: this.checkKeyBoardModifierOrAddMessage,
        }),
      ]),
    });
    this.map.updateSize();

    const content = this.popUpContent;
    const container = this.popUpContainer;
    const popup = new Overlay({
      element: container.nativeElement,
      autoPan: false,
    });
    this.map.addOverlay(popup);

    this.map.on('click', (ev) => {
      const feature = this.map.forEachFeatureAtPixel(ev.pixel, function (feat) {
        return feat;
      });
      if (feature && feature.getGeometry() instanceof Point) {
        const sensor = feature.get('sensor');
        if (sensor) {
          this.dialog.open(SensorDetailsDialogComponent, {
            width: '640px',
            data: {
              sensor,
              status: feature.get('sensorStatus') || null,
            },
          });
        }
      }
    });

    this.map.on('pointermove', (ev) => {
      const feature = this.map.forEachFeatureAtPixel(ev.pixel, function (feat) {
        return feat;
      });
      if (feature?.get('desc') && feature.getGeometry() instanceof Point) {
        const coordinate = ev.coordinate;
        content.nativeElement.innerHTML = feature.get('desc');
        popup.setPosition(coordinate);
      } else {
        popup.setPosition(undefined);
      }
    });

    await this.loadAndRenderSensors();
    this.startStatusPolling();
  }

  private checkKeyBoardModifierOrAddMessage = (event) => {
    if (platformModifierKeyOnly(event)) {
      return true;
    }
    if (event.type === 'pointermove') {
      return false;
    }
    return false;
  };

  private async loadAndRenderSensors() {
    const selectedSensorIds = new Set(
      (this.config.datasources || [])
        .filter((ds) => ds.checked)
        .map((ds) => ds.id)
    );

    const allSensors: SensorLocalizationSensor[] = await firstValueFrom(
      this.sensorsService.sensorsProjectRead(
        this.reportModuleService.currentReport.project
      )
    );

    if (this.destroyed) {
      return;
    }

    // New configurations contain sensor UUIDs. The influx_name comparison keeps
    // existing reports readable until they are saved once with the new format.
    const selectedSensors = allSensors.filter(
      (sensor) =>
        selectedSensorIds.has(sensor.id) ||
        selectedSensorIds.has(sensor.influx_name)
    );

    // Clear previous
    this.vectorSource.clear();

    const features: Feature[] = [];
    const renderedSensors: SensorLocalizationSensor[] = [];
    for (const sensor of selectedSensors) {
      if (!sensor.location_x || !sensor.location_y) {continue;}
      const lon = Number(sensor.location_x);
      const lat = Number(sensor.location_y);
      if (isNaN(lon) || isNaN(lat)) {continue;}

      // Transform using same approach as orthofoto: values are in
      // currentTransformationTo and need to be shown in currentTransformationFrom
      // Note the argument order [y, x] as used in orthofoto
      const mapCoord = proj4(
        this.reportModuleService.currentTransformationTo,
        this.reportModuleService.currentTransformationFrom
      ).inverse([lat, lon]);

      const sensorStatus = sensor.id
        ? this.sensorStatuses.get(sensor.id)
        : undefined;
      const desc = this.buildSensorPopup(sensor, sensorStatus);

      const feature = new Feature({
        geometry: new Point(mapCoord as any),
        desc,
        sensor,
        sensorStatus: sensorStatus || null,
      });
      feature.setStyle(this.buildSensorStyle(sensor, sensorStatus));
      features.push(feature);
      renderedSensors.push(sensor);
    }

    this.vectorSource.addFeatures(features);
    this.renderedSensors = renderedSensors;
    this.updateLegend();

    // Fit view to markers
    if (features.length > 0) {
      const extent = this.vectorSource.getExtent();
      this.map.getView().fit(extent, {
        size: this.map.getSize(),
        padding: [100, 100, 100, 100],
        maxZoom: 18,
      });
    }
    this.map.updateSize();
  }

  private startStatusPolling(): void {
    if (this.statusPollingStarted || this.destroyed) {
      return;
    }
    this.statusPollingStarted = true;

    timer(0, SENSOR_STATUS_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.alarmsService
            .getProjectSensorStatuses(
              this.reportModuleService.currentReport.project
            )
            .pipe(
              catchError((error) => {
                console.warn('Failed to refresh sensor alarm statuses', error);
                return EMPTY;
              })
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => this.applySensorStatuses(response.sensors));
  }

  private applySensorStatuses(statuses: SensorStatus[]): void {
    this.sensorStatuses.clear();
    for (const status of statuses || []) {
      if (status.sensor_id) {
        this.sensorStatuses.set(status.sensor_id, status);
      }
    }

    for (const feature of this.vectorSource.getFeatures()) {
      const sensor: SensorLocalizationSensor = feature.get('sensor');
      if (!sensor) {
        continue;
      }

      // Alarm data is deliberately joined only through the sensor UUID.
      const status = sensor.id
        ? this.sensorStatuses.get(sensor.id)
        : undefined;
      feature.set('sensorStatus', status || null);
      feature.set('desc', this.buildSensorPopup(sensor, status));
      feature.setStyle(this.buildSensorStyle(sensor, status));
    }

    this.vectorLayer.changed();
    this.updateLegend();
  }

  private updateLegend(): void {
    const legend = buildSensorLegend(
      this.renderedSensors,
      Array.from(this.sensorStatuses.values())
    );
    this.ngZone.run(() => {
      this.legend = legend;
    });
  }

  private buildSensorStyle(
    sensor: SensorLocalizationSensor,
    status?: SensorStatus
  ): Style {
    return new Style({
      image: this.buildShapeImage(
        sensor.symbol,
        this.getEffectiveColor(sensor, status)
      ),
      text: new Text({
        text: formatSensorMapLabel(
          sensor.display_name || sensor.influx_name,
          status?.max_severity
        ),
        backgroundFill: new Fill({ color: 'rgba(255,255,255,0.7)' }),
        padding: [2, 2, 2, 2],
        offsetX: 10,
        offsetY: 20,
        font: '0.8rem sans-serif',
        textAlign: 'start',
      }),
    });
  }

  private getEffectiveColor(
    sensor: SensorLocalizationSensor,
    status?: SensorStatus
  ): string {
    return getSensorStatusColor(
      sensor,
      status?.status || 'ok',
      status?.effective_color
    );
  }

  private buildShapeImage(symbol: string | null | undefined, color: string) {
    const stroke = new Stroke({ color: '#ffffff', width: 1 });
    const fill = new Fill({ color });
    const normalized = (symbol || '').toLowerCase();

    switch (normalized) {
      case 'triangle':
        return new RegularShape({
          points: 3,
          radius: 8,
          angle: 0,
          fill,
          stroke,
        });
      case 'square':
        return new RegularShape({
          points: 4,
          radius: 7,
          angle: Math.PI / 4, // oriented as a square
          fill,
          stroke,
        });
      case 'star':
        return new RegularShape({
          points: 5,
          radius: 9,
          radius2: 4.5,
          angle: 0,
          fill,
          stroke,
        });
      case 'circle':
      default:
        return new CircleStyle({
          radius: 6,
          fill,
          stroke,
        });
    }
  }

  private buildSensorPopup(
    sensor: SensorLocalizationSensor,
    status?: SensorStatus
  ): string {
    const esc = (v: unknown) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const header = esc(sensor.display_name || sensor.influx_name);
    const statusLabel = esc(this.getStatusLabel(status?.status));
    const statusColor = esc(this.getEffectiveColor(sensor, status));

    const rows: Array<{ k: string; v: string }> = [
      { k: 'Sensor-ID', v: esc(sensor.id) },
      { k: 'Messstation', v: esc(sensor.influx_name) },
      {
        k: 'Letzte Messung',
        v: status?.last_measurement_at
          ? esc(new Date(status.last_measurement_at).toLocaleString('de-AT'))
          : '—',
      },
    ];
    const dataStatus = status?.data_status || status?.measurement_status;
    if (dataStatus) {
      rows.push({
        k: 'Datenstatus',
        v: esc(this.getDataStatusLabel(dataStatus)),
      });
    }
    if (status?.alarm_level) {
      rows.push({
        k: 'Alarmstatus',
        v: esc(getAlarmLevelStatusLabel(status.alarm_level)),
      });
    }
    if (status?.max_severity !== null && status?.max_severity !== undefined) {
      rows.push({
        k: 'Maximal ausgelöst',
        v: esc(formatMaximumSeverity(status.max_severity)),
      });
    }

    const rowsHtml = rows
      .map(({ k, v }) => `<div><b>${k}</b>: ${v || '<i>—</i>'}</div>`)
      .join('');

    const alarmRowsHtml = (status?.active_alarms || [])
      .map((alarm) => {
        const context = [
          alarm.observed_at
            ? `Zeitpunkt: ${new Date(alarm.observed_at).toLocaleString('de-AT')}`
            : '',
          alarm.source ? `Quelle: ${alarm.source}` : '',
        ]
          .filter(Boolean)
          .map(esc)
          .join(' · ');

        return `
          <div style="margin-top:4px;">
            <div><b>${esc(alarm.metric)}</b>: ${esc(
              formatAlarmDescriptor(alarm)
            )}</div>
            <div><b>Vergleich</b>: ${esc(formatAlarmComparison(alarm))}</div>
            ${context ? `<div>${context}</div>` : ''}
          </div>
        `;
      })
      .join('');

    return `
      <div>
        <h4 style="margin:0 0 6px 0;">${header}</h4>
        <div style="margin-bottom:4px;">
          <span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${statusColor};"></span>
          <b>Status</b>: ${statusLabel}
        </div>
        ${rowsHtml}
        ${alarmRowsHtml ? `<div style="margin-top:4px;"><b>Aktive Warnungen und Alarme</b>${alarmRowsHtml}</div>` : ''}
      </div>
    `;
  }

  private getStatusLabel(status?: SensorStatus.StatusEnum): string {
    switch (status) {
      case 'alarm_level_2':
        return 'Alarm (Stufe 2)';
      case 'alarm_level_3':
        return 'Alarm (Stufe 3)';
      case 'stale':
        return 'Keine aktuellen Daten';
      case 'warning':
        return 'Warnung';
      case 'ok':
        return 'OK';
      default:
        return 'Nicht verfügbar';
    }
  }

  private getDataStatusLabel(
    status: SensorStatus.DataStatusEnum | SensorStatus.MeasurementStatusEnum
  ): string {
    switch (status) {
      case 'current':
      case 'available':
        return 'Aktuell';
      case 'stale':
        return 'Veraltet';
      case 'missing':
        return 'Keine Messung vorhanden';
      case 'unavailable':
        return 'Nicht verfügbar';
    }
  }
}
